/**
 * A character as the form holds it, against what the server keeps.
 *
 * The same split the items form uses and for the same reason: what somebody
 * types is text, half typed and often empty, and what the world stores is
 * numbers. Everything that turns one into the other lives here so the panel is
 * about the questions rather than about parsing.
 */

import type { Act } from './objects';

/** Somebody who fights, or somebody who does not. Everything else follows from it. */
export type CharacterKind = 'npc' | 'enemy';

export const RECIPE_FREE_CATEGORIES = [
  'townsfolk',
  'beast',
  'undead',
  'guard',
  'shopkeeper',
  'other',
] as const;

export const COMBAT_SKILLS = ['attack', 'strength', 'defence', 'ranged', 'magic'] as const;

export const BONUS_FIELDS = [
  ['attackStab', 'Stab'],
  ['attackSlash', 'Slash'],
  ['attackBash', 'Bash'],
  ['attackRanged', 'Ranged'],
  ['attackMagic', 'Magic'],
  ['defenceStab', 'Stab'],
  ['defenceSlash', 'Slash'],
  ['defenceBash', 'Bash'],
  ['defenceRanged', 'Ranged'],
  ['defenceMagic', 'Magic'],
] as const;

/** The four elements, as they are written in the content files. */
export const ELEMENTS = ['air', 'water', 'earth', 'fire'] as const;

/** One line of the list the server sends. */
export interface ListedCharacter {
  id: number;
  kind: CharacterKind;
  category: string;
  name: string;
}

/** A character as the server keeps it, which is the content file's own shape. */
export interface SavedCharacter {
  id: number;
  name: string;
  examine?: string;
  actions?: string[];
  /**
   * What each of those menu entries does, as the world holds it.
   *
   * Written loosely, because a file older than occasions carries acts without
   * one and they are filled in when the panel reads them.
   */
  acts?: (Partial<Act> & { action: string })[];
  combatLevel?: number;
  hitpoints?: number;
  aggressionRange?: number;
  wanderRange?: number;
  attackSpeed?: number;
  maxHit?: number;
  /** How far its blow carries, in tiles. One is the next tile. */
  reach?: number;
  /** What is seen crossing the ground when it strikes from a distance. */
  throws?: string;
  /** The element it suffers from, and the one it shrugs off. */
  weakTo?: string;
  strongTo?: string;
  levels?: Record<string, number>;
  bonuses?: Record<string, number>;
  respawnTicks?: number;
  category?: string;
  lines?: string[];
  boss?: boolean;
  /** Its own sounds, by names in the sound library. Empty is the usual one. */
  sounds?: { attack?: string; hurt?: string; die?: string };
  model?: string;
  models?: string[];
  skin?: number;
  eyes?: number;
  size?: number;
  dialogue?: unknown[];
}

export interface CharacterDetail {
  character: SavedCharacter;
  /** What it leaves behind, as the drop editor writes it. Read only here. */
  drops: { entries?: { itemId: number; min: number; max: number; weight: number }[] } | null;
}

export interface CharacterDraft {
  id: number;
  kind: CharacterKind;
  name: string;
  examine: string;
  category: string;
  actions: string;
  /**
   * What each of those menu entries does.
   *
   * The same list scenery carries. Without it a character can be talked to,
   * fought or traded with, and anything else on the menu is a word that does
   * nothing when it is pressed.
   */
  acts: Act[];
  model: string;
  variants: string[];
  skin: string;
  eyes: string;
  size: string;
  wanderRange: string;
  respawnTicks: string;
  lines: string[];
  /** Everything below is only asked about for something that fights. */
  combatLevel: string;
  hitpoints: string;
  maxHit: string;
  attackSpeed: string;
  reach: string;
  throws: string;
  aggressionRange: string;
  /** Empty for a character that is no better or worse against any element. */
  weakTo: string;
  strongTo: string;
  boss: boolean;
  sounds: { attack: string; hurt: string; die: string };
  levels: Record<string, string>;
  bonuses: Record<string, string>;
}

const number = (text: string, fallback = 0): number => {
  const held = Number(text);
  return Number.isFinite(held) ? held : fallback;
};

/** A blank character of a kind, with the defaults that kind usually wants. */
export function blankCharacter(kind: CharacterKind): CharacterDraft {
  const fighting = kind === 'enemy';

  return {
    id: 0,
    kind,
    name: '',
    examine: '',
    category: fighting ? 'beast' : 'townsfolk',
    actions: fighting ? 'Attack' : 'Talk to',
    acts: [],
    model: fighting ? 'goblin' : 'person',
    variants: [],
    skin: '0',
    eyes: '0',
    size: '1',
    wanderRange: fighting ? '4' : '2',
    respawnTicks: '50',
    lines: fighting ? [] : ['Good day.'],
    combatLevel: fighting ? '5' : '0',
    hitpoints: fighting ? '12' : '1',
    maxHit: fighting ? '2' : '0',
    attackSpeed: '4',
    reach: '1',
    throws: '',
    aggressionRange: fighting ? '4' : '0',
    weakTo: '',
    strongTo: '',
    boss: false,
    sounds: { attack: '', hurt: '', die: '' },
    levels: Object.fromEntries(COMBAT_SKILLS.map((skill) => [skill, fighting ? '5' : '1'])),
    bonuses: Object.fromEntries(BONUS_FIELDS.map(([field]) => [field, '0'])),
  };
}

/** The kind of a saved character, which is only ever whether it fights. */
export function kindOfSaved(character: SavedCharacter): CharacterKind {
  return (character.combatLevel ?? 0) > 0 ? 'enemy' : 'npc';
}

/** Reads a saved character back into the form. */
export function draftFrom(character: SavedCharacter): CharacterDraft {
  const kind = kindOfSaved(character);
  const blank = blankCharacter(kind);

  const levels = { ...blank.levels };
  for (const skill of COMBAT_SKILLS) {
    const held = character.levels?.[skill];
    if (typeof held === 'number') levels[skill] = String(held);
  }

  const bonuses = { ...blank.bonuses };
  for (const [field] of BONUS_FIELDS) {
    const held = character.bonuses?.[field];
    if (typeof held === 'number') bonuses[field] = String(held);
  }

  const models = character.models ?? [];

  return {
    ...blank,
    id: character.id,
    kind,
    name: character.name,
    examine: character.examine ?? '',
    category: character.category ?? blank.category,
    actions: (character.actions ?? []).join(', '),
    // Filled in rather than taken as found: a character written before acts
    // had occasions carries none, and the panel needs both fields present.
    acts: (character.acts ?? []).map((act) => ({
      on: act.on ?? 'menu',
      atHealth: act.atHealth ?? 50,
      action: act.action,
      when: act.when ?? [],
      does: act.does ?? [],
      refuses: act.refuses ?? '',
      hidden: act.hidden === true,
      once: 'always',
      doneSays: '',
    })),
    model: models[0] ?? character.model ?? blank.model,
    variants: models.slice(1),
    skin: String(character.skin ?? 0),
    eyes: String(character.eyes ?? 0),
    size: String(character.size ?? 1),
    wanderRange: String(character.wanderRange ?? blank.wanderRange),
    respawnTicks: String(character.respawnTicks ?? 50),
    lines: [...(character.lines ?? [])],
    combatLevel: String(character.combatLevel ?? 0),
    hitpoints: String(character.hitpoints ?? 1),
    maxHit: String(character.maxHit ?? 0),
    attackSpeed: String(character.attackSpeed ?? 4),
    reach: String(character.reach ?? 1),
    throws: character.throws ?? '',
    aggressionRange: String(character.aggressionRange ?? 0),
    weakTo: character.weakTo ?? '',
    strongTo: character.strongTo ?? '',
    boss: character.boss === true,
    sounds: {
      attack: character.sounds?.attack ?? '',
      hurt: character.sounds?.hurt ?? '',
      die: character.sounds?.die ?? '',
    },
    levels,
    bonuses,
  };
}

/**
 * The character the server is asked to keep, built from what was typed.
 *
 * Whatever conversation the character already has is carried through untouched:
 * it is written in its own panel, and a form that did not know about it would
 * throw it away every time somebody corrected a spelling.
 */
export function characterFrom(draft: CharacterDraft, held: SavedCharacter | null): SavedCharacter {
  const actions = draft.actions
    .split(',')
    .map((action) => action.trim())
    .filter((action) => action.length > 0);

  const fighting = draft.kind === 'enemy';
  const variants = draft.variants.filter((name) => name !== draft.model);

  const character: SavedCharacter = {
    id: draft.id,
    name: draft.name.trim(),
    examine: draft.examine.trim(),
    category: draft.category.trim() || 'other',
    actions,
    // What each of those entries does, written whole. An act is edited as a
    // whole thing rather than a field at a time, so a patch would be a second
    // shape to keep in step.
    acts: draft.acts,
    model: draft.model,
    models: variants.length > 0 ? [draft.model, ...variants] : [],
    skin: Math.max(0, Math.round(number(draft.skin))),
    eyes: Math.max(0, Math.round(number(draft.eyes))),
    size: Math.min(5, Math.max(1, Math.round(number(draft.size, 1)))),
    wanderRange: Math.max(0, Math.round(number(draft.wanderRange))),
    respawnTicks: Math.min(10_000, Math.max(1, Math.round(number(draft.respawnTicks, 50)))),
    lines: draft.lines.map((line) => line.slice(0, 160)).filter((line) => line.length > 0),
    combatLevel: fighting ? Math.max(1, Math.round(number(draft.combatLevel, 1))) : 0,
    hitpoints: Math.max(1, Math.round(number(draft.hitpoints, 1))),
    maxHit: fighting ? Math.max(0, Math.round(number(draft.maxHit))) : 0,
    attackSpeed: Math.min(10, Math.max(1, Math.round(number(draft.attackSpeed, 4)))),
    reach: Math.min(10, Math.max(1, Math.round(number(draft.reach, 1)))),
    throws: draft.throws.trim().slice(0, 24),
    aggressionRange: fighting ? Math.max(0, Math.round(number(draft.aggressionRange))) : 0,
    ...(fighting && draft.weakTo ? { weakTo: draft.weakTo } : {}),
    ...(fighting && draft.strongTo ? { strongTo: draft.strongTo } : {}),
    boss: fighting && draft.boss,
    sounds: draft.sounds,
    levels: Object.fromEntries(
      COMBAT_SKILLS.map((skill) => [
        skill,
        Math.min(99, Math.max(1, Math.round(number(draft.levels[skill] ?? '1', 1)))),
      ]),
    ),
    bonuses: Object.fromEntries(
      BONUS_FIELDS.map(([field]) => [field, Math.round(number(draft.bonuses[field] ?? '0'))]),
    ),
    dialogue: held?.dialogue ?? [],
  };

  return character;
}

/** What is wrong with a draft, or nothing when it is ready to be kept. */
export function problemWith(draft: CharacterDraft): string | null {
  if (draft.name.trim().length === 0) return 'They need a name.';
  if (draft.name.trim().length > 48) return 'That name is too long.';
  if (draft.kind === 'enemy' && Math.round(Number(draft.hitpoints) || 0) < 1) {
    return 'Something that fights needs at least one point of health.';
  }
  if (draft.kind === 'npc' && draft.actions.trim().length === 0) {
    return 'Give them something a right click can do, even if it is only Talk to.';
  }
  return null;
}

/** Reads the list the server sends: one line per character. */
export function readCharacterList(written: string): ListedCharacter[] {
  const found: ListedCharacter[] = [];

  for (const line of written.split('\n')) {
    const [id, kind, category, ...rest] = line.split('|');
    if (!id || !kind || rest.length === 0) continue;
    found.push({
      id: Number(id),
      kind: kind === 'enemy' ? 'enemy' : 'npc',
      category: category ?? 'other',
      name: rest.join('|'),
    });
  }

  return found;
}

/** Reads one character out of what the server answered. */
export function readCharacterDetail(packet: {
  text: Record<string, string>;
}): CharacterDetail | null {
  try {
    const character = JSON.parse(packet.text.json ?? '') as SavedCharacter;
    const drops = packet.text.drops
      ? (JSON.parse(packet.text.drops) as CharacterDetail['drops'])
      : null;
    return { character, drops };
  } catch {
    return null;
  }
}
